<template>
  <div class="add-vacation-container">
    <b-row>
      <b-col cols="6">
        <el-date-picker
          id="vacation-start"
          v-model="startDate"
          :picker-options="startDatePickerOptions"
          :class="{ 'red-border': isInvalidDate || isMissingDate }"
          :format="defaultDateFormat"
          :value-format="defaultDateValueFormat"
          :disabled="isDisabledStartDate || isFormDisabled"
          :placeholder="$getText('PRESENCE_VACATION_FR0M')"
          type="date"
          @change="
            isMissingDate = false;
            isInvalidDate = false;
          "
          @focus="datePickerModifier()"
        />
      </b-col>
      <b-col cols="6">
        <el-date-picker
          id="vacation-end"
          v-model="endDate"
          :picker-options="endDatePickerOptions"
          :class="{ 'red-border': isInvalidDate || isMissingDate }"
          :format="defaultDateFormat"
          :value-format="defaultDateValueFormat"
          :disabled="isFormDisabled"
          :placeholder="$getText('PRESENCE_VACATION_TO')"
          type="date"
          @change="
            isMissingDate = false;
            isInvalidDate = false;
          "
          @focus="datePickerModifier()"
        />
      </b-col>
      <b-col v-if="isInvalidDate || isMissingDate || isVacationTooLong" class="mt-1" cols="12">
        <b-alert variant="danger" show>
          {{ $getText('PRESENCE_REGISTER_VACATION_WARNING') }}
        </b-alert>
      </b-col>
      <b-col class="mt-3" cols="12">
        <b-textarea
          id="vacation-comment"
          v-model="vacationComment"
          :disabled="isFormDisabled"
          placeholder="Evt. bemærkning"
          maxlength="250"
          class="dark-bg"
        />
        <b-btn
          v-if="!isFormDisabled"
          :variant="isEmployeeDashboard ? 'primary' : 'white'"
          class="pull-right add-vacation-button mt-2"
          :disabled="isLoading"
          @click="onClickSaveVacation()"
        >
          {{ $t('button.save') }}
        </b-btn>
        <b-btn
          v-if="!isFormDisabled"
          variant="link"
          class="pull-right cancel-vacation-button mt-2 mr-2"
          @click="cancelForm"
        >
          {{ $t('button.cancel') }}
        </b-btn>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { presenceStates } from '../../enums/presenceStates';
import { presenceModules } from '../../enums/presenceModules';
import { types } from '../../../src_presence/store/types/types';
import moment from 'moment-timezone';
import { useVacationDatePicker } from '../../composables/useVacationDatePicker';
import { eventTypeEnum } from '../../enums/eventTypeEnum';

export default {
  props: {
    childIds: { type: Array, default: () => [] },
    isDisabledStartDate: { type: Boolean, default: true },
    isFormDisabled: { type: Boolean, default: false },
    vacationFormData: { type: Object, default: null },
    isChildOnVacation: { type: Boolean, default: false },
  },
  setup() {
    const { startDate, endDate, startDatePickerOptions, endDatePickerOptions, isEndDateValid } =
      useVacationDatePicker();

    return {
      startDate,
      endDate,
      startDatePickerOptions,
      endDatePickerOptions,
      isEndDateValid,
    };
  },
  data: function () {
    return {
      presenceStates: presenceStates,
      presenceModules: presenceModules,
      isLoading: false,
      vacationComment: null,
      isMissingDate: false,
      isInvalidDate: false,
      isVacationTooLong: false,
    };
  },
  computed: {
    ...mapGetters({
      defaultDateFormat: types.GET_DEFAULT_DATE_FORMAT,
      defaultDateValueFormat: types.GET_DEFAULT_DATE_VALUE_FORMAT,
      presenceConfiguration: types.GET_PRESENCE_CONFIGURATION,
    }),
    isEmployeeDashboard() {
      return this.$route.path.indexOf('kommegaa/medarbejder') != -1;
    },
  },
  methods: {
    ...mapActions({
      addVacationInCheckInDashboard: types.CHECK_IN_DASHBOARD_ADD_VACATION,
      addVacationInEmployeeDashboard: types.ADD_VACATION_FOR_CHILDREN,
      updateSimpleEvent: types.UPDATE_SIMPLE_EVENT,
    }),
    cancelForm() {
      this.$emit('cancelVacationForm');
    },
    updateStatusAfterAddedVacation() {
      this.$emit('saveVacation');
      if (moment(this.startDate).isSame(moment(), 'day')) {
        this.$emit('updateChildStatus', presenceStates.REPORTED_ABSENT);
      }
    },
    addNewVacation() {
      const params = {
        childIds: this.childIds,
        intervals: [
          {
            startDate: this.startDate,
            endDate: this.endDate,
          },
        ],
        comment: this.vacationComment,
      };
      const addVacation = this.isEmployeeDashboard
        ? this.addVacationInEmployeeDashboard
        : this.addVacationInCheckInDashboard;
      return addVacation(params);
    },
    updateVacation() {
      const params = {
        eventId: this.vacationFormData.id,
        startDateTime: this.startDate,
        endDateTime: this.endDate,
        type: eventTypeEnum.PRESENCE_HOLIDAY,
        description: this.vacationComment,
        title: this.vacationFormData.title,
        allDay: true,
        private: false,
        responseRequired: false,
        addedToInstitutionCalendar: false,
        hideInOwnCalendar: false,
        isEditEvent: true,
      };
      return this.updateSimpleEvent(params);
    },
    validate() {
      this.isMissingDate = !this.startDate || !this.endDate;

      const isValid = this.isEndDateValid();

      this.isInvalidDate = !isValid;
      this.isVacationTooLong = !isValid;
    },
    async onClickSaveVacation() {
      this.validate();
      if (!this.isInvalidDate && !this.isMissingDate) {
        this.isLoading = true;
        if (this.isChildOnVacation) {
          await this.updateVacation();
        } else {
          await this.addNewVacation();
        }
        this.updateStatusAfterAddedVacation();
        this.isLoading = false;
      }
    },
  },
  mounted() {
    if (this.vacationFormData) {
      if (this.isChildOnVacation) {
        this.startDate = moment(this.vacationFormData.startDateTime).utc().format('YYYY-MM-DD');
        this.endDate = moment(this.vacationFormData.endDateTime).utc().format('YYYY-MM-DD');
        this.vacationComment = this.vacationFormData.description?.html ?? null;
      } else {
        this.startDate = this.vacationFormData.startDateTime;
      }
    } else {
      this.startDate = moment().format('YYYY-MM-DD');
    }
  },
};
</script>
<style lang="scss" scoped>
@use '../../assets/scss/core/variables' as *;
@use '../../assets/scss/core/breakpoints' as *;
.add-vacation-container {
  flex: 1;
  @include breakpoint-lg-down() {
    flex: unset;
    width: 100%;
  }
  .el-date-editor {
    width: 100%;
  }
  .cancel-vacation-button {
    color: $color-white;
    padding: 14px 44px;
    &:hover {
      background-color: var(--color-primary-base);
    }
    .theme-employee & {
      color: $color-grey;
    }
  }
  input.form-control {
    width: 100%;
    &::placeholder {
      /* Chrome, Firefox, Opera, Safari 10.1+ */
      font-size: 18px;
    }
    &:-ms-input-placeholder {
      /* Internet Explorer 10-11 */
      font-size: 18px;
    }
    &::-ms-input-placeholder {
      /* Microsoft Edge */
      font-size: 18px;
    }
  }
  @include breakpoint-lg-down() {
    margin-top: 20px;
  }
}
</style>
